'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
  onProjectNameChange: (name: string) => void;
  projectDescription?: string;
  onProjectDescriptionChange?: (description: string) => void;
  onCreateProject: () => void;
  context?: 'standalone' | 'assign' | 'reassign';
  isLoading?: boolean;
  title?: string;
  buttonText?: string;
  loadingText?: string;
}

const MAX_PROJECT_NAME_LENGTH = 35;
const MAX_PROJECT_DESCRIPTION_LENGTH = 2000;

export function CreateProjectModal({
  isOpen,
  onClose,
  projectName,
  onProjectNameChange,
  projectDescription = '',
  onProjectDescriptionChange,
  onCreateProject,
  context = 'standalone',
  isLoading = false,
  title = 'Create a new project',
  buttonText,
  loadingText,
}: CreateProjectModalProps) {
  const [didInitialFocus, setDidInitialFocus] = useState(false);
  const isNameTooLong = projectName.length > MAX_PROJECT_NAME_LENGTH;
  const isDescriptionTooLong =
    projectDescription.length > MAX_PROJECT_DESCRIPTION_LENGTH;

  const handleInitialFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (didInitialFocus) return;
    const target = e.target as HTMLInputElement;
    setTimeout(() => {
      try {
        const end = target.value.length;
        target.setSelectionRange(end, end);
      } catch {}
      setDidInitialFocus(true);
    }, 0);
  };

  // Reset the initial-focus guard when the dialog is opened
  useEffect(() => {
    if (isOpen) {
      setDidInitialFocus(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <label
              htmlFor="project-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Project name
            </label>
            <Input
              id="project-name"
              value={projectName}
              onChange={(e) => onProjectNameChange(e.target.value)}
              onFocus={handleInitialFocus}
              placeholder="Enter project name"
              disabled={isLoading}
              className={`w-full bg-white dark:bg-white text-gray-900 ${isNameTooLong ? '!border-red-500 focus-visible:!ring-transparent' : ''}`}
              aria-invalid={isNameTooLong}
            />
            {isNameTooLong && (
              <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                Project name must be {MAX_PROJECT_NAME_LENGTH} characters or
                fewer. ({projectName.length}/{MAX_PROJECT_NAME_LENGTH})
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="project-description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Project description (optional)
            </label>
            <Textarea
              id="project-description"
              value={projectDescription}
              onChange={(e) => onProjectDescriptionChange?.(e.target.value)}
              placeholder="Enter project description"
              disabled={isLoading}
              className={`w-full ${isDescriptionTooLong ? '!border-red-500 focus-visible:!ring-transparent' : ''}`}
              rows={3}
              aria-invalid={isDescriptionTooLong}
            />
            {isDescriptionTooLong && (
              <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                Project description must be {MAX_PROJECT_DESCRIPTION_LENGTH}{' '}
                characters or fewer. ({projectDescription.length}/
                {MAX_PROJECT_DESCRIPTION_LENGTH})
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="flex-row justify-end space-x-2 pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2"
          >
            Cancel
          </Button>
          <Button
            onClick={onCreateProject}
            disabled={
              !projectName.trim() ||
              isLoading ||
              isNameTooLong ||
              isDescriptionTooLong
            }
            className={`px-4 py-2 ${
              projectName.trim() &&
              !isLoading &&
              !isNameTooLong &&
              !isDescriptionTooLong
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {loadingText || 'Creating...'}
              </div>
            ) : buttonText ? (
              buttonText
            ) : context === 'reassign' ? (
              'Create and Reassign'
            ) : context === 'assign' ? (
              'Create and Assign'
            ) : (
              'Create Project'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
